<template>
  <section class="px-2">
    <div class="container">
      <div class="row">
        <div class="col-8 offset-2 text-center">
          <h1>Vos dernières attestations</h1>
        </div>
        <div class="col-12 mt-3 mb-4 d-flex align-items-center justify-content-center position-relative">
          <ul class="nav nav-pills custom-pills">
            <li class="nav-item">
              <a
                class="nav-link cursor-pointer fw-bold"
                :class="{ active: activeTab === 'CURRENT' }"
                @click="activeTab = 'CURRENT'"
              >
                Période en cours
              </a>
            </li>
            <li class="nav-item ms-2">
              <a
                class="nav-link cursor-pointer fw-bold"
                :class="{ active: activeTab === 'PREVIOUS' }"
                @click="activeTab = 'PREVIOUS'"
              >
                Périodes précédentes
              </a>
            </li>
          </ul>
          <div
            v-if="contracts.length > 0"
            class="d-flex gap-2 ms-3 filter-dropdowns"
          >
            <v-filter-dropdown
              :value="selectedProductId"
              :options="productDropdownOptions"
              placeholder="Contrat"
              @input="onProductChange"
            />
            <v-filter-dropdown
              :value="selectedCertType"
              :options="certTypeDropdownOptions"
              placeholder="Type"
              @input="onCertTypeChange"
            />
            <v-filter-dropdown
              :value="selectedYear"
              :options="yearDropdownOptions"
              placeholder="Année"
              @input="onYearChange"
            />
          </div>
        </div>
        <v-display
          v-for="contract in certificatesContainer"
          :key="contract.id"
          :subscription="contract"
          :active-tab="activeTab"
          :certificate-type-filter="selectedCertType"
          class="mb-2"
        />
      </div>
    </div>
    <v-no-resource v-if="contracts.length === 0">
      <div class="d-flex flex-column">
        <div class="lead">
          Vous n’avez aucune attestation, car vous n’avez pas encore de contrat chez nous !
        </div>
        <div class="mt-2">
          <a
            :href="getAppUrl()"
            class="btn btn-primary fs-5 fw-bold"
          >
            Ajouter un nouveau contrat
          </a>
        </div>
      </div>
    </v-no-resource>
  </section>
</template>
<script>
import { mapGetters } from 'vuex';
import { uniq } from 'lodash-es';

import CertificateDisplay from '@/components/documents/helpers/CertificateDisplay.vue';
import FilterDropdown from '@/components/common/filters/FilterDropdown.vue';

export default {
  name: 'CertificateView',
  components: {
    'v-display': CertificateDisplay,
    'v-filter-dropdown': FilterDropdown,
  },
  data() {
    return {
      activeTab: 'CURRENT',
      certificatesContainer: [],
      selectedProductId: null,
      selectedCertType: null,
      selectedYear: null,
    };
  },
  computed: {
    ...mapGetters({
      shadowContracts: 'contracts/get',
      products: 'rater/products',
    }),
    allYears() {
      const years = this.contracts
        .map(({ effectiveDate }) => effectiveDate)
        .map((date) => new Date(date).getFullYear())
        .sort((a, b) => a - b);
      return uniq(years);
    },
    allProducts() {
      return uniq(this.contracts.map(({ productId }) => productId));
    },
    productOptions() {
      return this.allProducts.map((productId) => {
        const { name } = this.products.find(({ id }) => id === productId) || { name: '' };
        return { id: productId, name };
      });
    },
    productDropdownOptions() {
      return this.productOptions.map(({ id, name }) => ({ value: id, label: name }));
    },
    yearDropdownOptions() {
      return this.allYears.map((y) => ({ value: y, label: String(y) }));
    },
    allCertTypes() {
      const types = this.contracts
        .flatMap(({ documents }) => (documents || []).map(({ type }) => type))
        .filter((t) => t && t.startsWith('CERTIFICATE'));
      return uniq(types);
    },
    certTypeDropdownOptions() {
      const labels = {
        CERTIFICATE: 'Attestation principale',
        CERTIFICATE_ACTIVITY: 'Attestation Orias',
        CERTIFICATE_ACI: 'Agent commercial immobilier',
        CERTIFICATE_RDD: 'Responsabilité des dirigeants',
        CERTIFICATE_AI: 'Agent immobilier',
        CERTIFICATE_GARFI: 'Garantie financière',
        CERTIFICATE_VISA: 'Mission',
      };
      return this.allCertTypes.map((t) => ({ value: t, label: labels[t] || t }));
    },
    contracts() {
      return this.shadowContracts || [];
    },
  },
  watch: {
    contracts: {
      handler() {
        this.applyFilterOnContracts();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.applyFilterOnContracts();
  },
  methods: {
    getAppUrl() {
      return import.meta.env.VITE_WIDGET_URL;
    },

    onProductChange(value) {
      this.selectedProductId = value;
      this.applyFilterOnContracts();
    },
    onCertTypeChange(value) {
      this.selectedCertType = value;
      this.applyFilterOnContracts();
    },
    onYearChange(value) {
      this.selectedYear = value;
      this.applyFilterOnContracts();
    },

    filterByType({ productId }) {
      if (this.selectedProductId === null) return true;
      return productId === this.selectedProductId;
    },
    filterByYears({ effectiveDate }) {
      if (this.selectedYear === null) return true;
      const year = new Date(effectiveDate).getFullYear();
      return year === this.selectedYear;
    },

    applyFilterOnContracts() {
      const shadowContainer = this.contracts
        .filter(({ status }) => status !== 'QUOTED')
        .filter(this.filterByYears)
        .filter(this.filterByType);
      this.sortByDateAsc(shadowContainer);
      this.certificatesContainer = shadowContainer;
    },

    sortByDate(object, isAsc) {
      return object.sort(({ effectiveDate: a }, { effectiveDate: b }) => {
        const aFullYear = new Date(a).getTime();
        const bFullYear = new Date(b).getTime();
        return isAsc ? bFullYear - aFullYear : aFullYear - bFullYear;
      });
    },
    sortByDateAsc(object) {
      return this.sortByDate(object, true);
    },
  },
};
</script>

<style scoped>
.custom-pills .nav-link {
  color: #3f83f8;
  background-color: transparent;
  border-radius: 9999px;
  padding: 0.5rem 1rem;
}
.custom-pills .nav-link.active {
  color: #fff;
  background-color: #3f83f8;
}

@media screen and (max-width: 767px) {
  .filter-dropdowns {
    position: static !important;
    margin-top: 8px;
    margin-left: 0 !important;
    justify-content: center;
  }
}
</style>
